/**
 * pages/Layout.js — Persistent application shell (header + navigation)
 * ======================================================================
 * Rendered as the parent Route in App.js; child page content is injected
 * via React Router's <Outlet />.
 *
 * Responsibilities
 * -----------------
 * - Sticky, frosted-glass header with the FNMA brand and animated subtitle.
 * - Role-based navigation: links shown depend on the user's role (see NAV_ITEMS).
 * - Google One-Tap login button — delegates to apiClient.login() on success
 *   and persists the returned usr object into context + localStorage.
 * - Responsive: desktop shows an inline nav bar; mobile (< sm breakpoint)
 *   collapses to a hamburger button that opens a Drawer.
 * - Displays the logged-in user's role badge and avatar in the header.
 *
 * Role → navigation mapping (NAV_ITEMS)
 * ----------------------------------------
 * ADMIN        → Home | Users | Search
 * FACT_CHECKER → Home | Newsfeed | Search
 * USER         → Home | Newsfeed | Search
 * GUEST        → Home  (no token)
 */

import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { Typography, Avatar, Tag, Button, Drawer, Space, Grid } from "antd";
import { MenuOutlined, UserOutlined } from "@ant-design/icons";
import { GoogleLogin } from "@react-oauth/google";
import { TypeAnimation } from "react-type-animation";
import { useState } from "react";
import { useLoc, useUsr } from "../App";
import apiClient from "../utils/apiClient";

const { Text } = Typography;
const { useBreakpoint } = Grid;

// Ant Design tag colour for each role badge displayed in the header
const ROLE_TAG_COLOR = { ADMIN: "gold", FACT_CHECKER: "geekblue", USER: "cyan" };

// Navigation items per role.  GUEST is the unauthenticated fallback.
const NAV_ITEMS = {
    ADMIN: [{ key: "/", label: "Home" }, { key: "/news_feed", label: "Newsfeed" }, { key: "/user_list", label: "Users" }],
    FACT_CHECKER: [{ key: "/", label: "Home" }, { key: "/news_feed", label: "Newsfeed" }, { key: "/search", label: "Search" }],
    USER: [{ key: "/", label: "Home" }, { key: "/news_feed", label: "Newsfeed" }, { key: "/search", label: "Search" }],
    GUEST: [{ key: "/", label: "Home" }],
};

/**
 * NavLinks — renders the navigation button list.
 *
 * @param {boolean}  mobile      — true = vertical column layout (Drawer).
 * @param {object[]} items       — array of { key, label } nav entries.
 * @param {string}   currentPath — current route pathname for active highlighting.
 * @param {function} onNavigate  — optional callback after navigation (closes Drawer).
 */
const NavLinks = ({ mobile = false, items, currentPath, onNavigate }) => {
    const navigate = useNavigate();
    return (
        <div style={{ display: "flex", flexDirection: mobile ? "column" : "row", gap: mobile ? 4 : 0 }}>
            {items.map(item => {
                const isActive = currentPath === item.key;
                return (
                    <Button
                        key={item.key}
                        type="text"
                        onClick={() => { navigate(item.key); onNavigate?.(); }}
                        style={{
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? "#1677ff" : "#262626",
                            borderRadius: mobile ? 6 : 0,
                            // Desktop active state: blue underline; mobile: blue tinted background
                            borderBottom: !mobile && isActive ? "2px solid #1677ff" : "none",
                            padding: mobile ? "10px 16px" : "0 16px",
                            height: mobile ? 44 : 60,
                            width: mobile ? "100%" : "auto",
                            textAlign: mobile ? "left" : "center",
                            background: mobile && isActive ? "#e6f4ff" : "transparent",
                            transition: "color 0.15s, background 0.15s",
                        }}
                    >
                        {item.label}
                    </Button>
                );
            })}
        </div>
    );
};

/**
 * Layout — the persistent page shell rendered for every route.
 *
 * Reads usr from LoginContext to determine which nav items to show and
 * whether to display the role badge and avatar.
 *
 * Reads loc from LocationContext to pass device coordinates to the login call.
 */
export const Layout = () => {
    const location = useLocation();
    const screens = useBreakpoint();       // Ant Design responsive breakpoint map
    const { loc } = useLoc();
    const { usr, setUsr } = useUsr();
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Determine which nav items to show based on role; fall back to USER nav
    // if the role is unrecognised (defensive), and GUEST if not logged in.
    const navItems = usr.token ? (NAV_ITEMS[usr.role] || NAV_ITEMS.USER) : NAV_ITEMS.GUEST;

    /**
     * loggedIn — callback fired by GoogleLogin on successful authentication.
     * Exchanges the Google JWT credential for a backend session token by calling
     * apiClient.login(), then stores the returned usr object in context and
     * localStorage.
     *
     * @param {object} response — Google OAuth response containing `credential` (JWT string).
     */
    const loggedIn = async (response) => {
        try {
            const { credential } = response;
            const loginResponse = await apiClient.login(credential, loc);
            setUsr(loginResponse.data);
            // Persist immediately so the session survives a full page reload
            localStorage.setItem("usr", JSON.stringify(loginResponse.data));
        } catch (error) {
            console.error("Error during login:", error);
        }
    };

    return (
        <>
            {/* ----------------------------------------------------------------
                Sticky header — frosted-glass effect via backdrop-filter blur.
                z-index 200 keeps it above the Leaflet map (z-index 50).
            ---------------------------------------------------------------- */}
            <header style={{
                position: "sticky", top: 0, zIndex: 200, height: 60,
                background: "rgba(255,255,255,0.88)",
                backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
                borderBottom: "1px solid rgba(0,0,0,0.07)",
                display: "flex", alignItems: "center",
                padding: "0 clamp(16px, 4vw, 48px)", gap: 16,
            }}>
                {/* Brand logo — fixed width to prevent layout shift */}
                <div style={{ display: "flex", flexDirection: "column", cursor: "pointer", userSelect: "none", flexShrink: 0, width: 144 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <Text strong style={{ fontSize: 17, letterSpacing: "0.06em", color: "#1677ff", lineHeight: 1.1 }}>FNMA</Text>
                        {/* Country flag — commented out; re-enable if needed */}
                        <img
                            src="https://upload.wikimedia.org/wikipedia/en/4/41/Flag_of_India.svg"
                            alt="IN"
                            title="India"
                            height={14}
                            style={{ borderRadius: 1, boxShadow: '0 1px 2px rgba(0,0,0,0.12)', flexShrink: 0 }}
                        />
                    </div>
                    {/* Animated subtitle — only shown on sm+ screens to save space */}
                    {screens.sm && (
                        <div style={{ fontSize: 9, color: "#aaa", height: 13, overflow: "hidden" }}>
                            <TypeAnimation sequence={["Fake News Archive", 2500, "Misinformation Tracker", 2500]} repeat={Infinity} style={{ fontSize: 9 }} />
                        </div>
                    )}
                </div>

                {/* Desktop navigation — hidden on mobile via CSS class */}
                <div className="desktop-nav" style={{ flex: 1, justifyContent: "center" }}>
                    <NavLinks items={navItems} currentPath={location.pathname} />
                </div>

                {/* Right side: role badge, avatar, Google login button, hamburger */}
                <Space size={10} style={{ flexShrink: 0, marginLeft: "auto" }}>
                    {/* Role badge — only shown when logged in on sm+ screens */}
                    {usr.token && usr.role && screens.sm && (
                        <Tag color={ROLE_TAG_COLOR[usr.role] || "default"} style={{ margin: 0, fontWeight: 500 }}>
                            {usr.role.replace("_", " ")}
                        </Tag>
                    )}
                    {/* User avatar — shows email on hover */}
                    {usr.token && (
                        <Avatar size={30} icon={<UserOutlined />} style={{ background: "#1677ff", cursor: "default" }} title={usr.email} />
                    )}
                    {/* Google login button — shows "continue_with" text if already logged in */}
                    <GoogleLogin text={usr.token ? "continue_with" : "signin_with"} auto_select flow="auth-code" onSuccess={loggedIn} onError={e => console.error(e)} useOneTap />
                    {/* Hamburger — only visible on mobile (controlled by App.css .mobile-menu-btn) */}
                    <Button type="text" icon={<MenuOutlined />} className="mobile-menu-btn" onClick={() => setDrawerOpen(true)} />
                </Space>
            </header>

            {/* ----------------------------------------------------------------
                Mobile navigation drawer — slides in from the right.
                Contains the same NavLinks as the desktop header, plus the
                user's email at the bottom for context.
            ---------------------------------------------------------------- */}
            <Drawer
                title={
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Text strong style={{ color: "#1677ff", fontSize: 15, letterSpacing: "0.06em" }}>FNMA</Text>
                        {usr.token && usr.role && <Tag color={ROLE_TAG_COLOR[usr.role] || "default"} style={{ margin: 0 }}>{usr.role.replace("_", " ")}</Tag>}
                    </div>
                }
                placement="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} width={220}
            >
                {/* onNavigate closes the drawer after the route changes */}
                <NavLinks mobile items={navItems} currentPath={location.pathname} onNavigate={() => setDrawerOpen(false)} />
                {/* Show the logged-in email at the bottom of the drawer */}
                {usr.token && (
                    <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 20, paddingTop: 16, borderTop: "1px solid #f0f0f0", wordBreak: "break-all" }}>
                        {usr.email}
                    </Text>
                )}
            </Drawer>

            {/* Child route content renders here */}
            <main><Outlet /></main>
        </>
    );
};
